<!-- ─── Card 1: Configuration Type ──────────────────────────────────────────── -->
<div class="card p-4 mb-4">
  <p class="fw-semibold mb-1 section-title">
    Applicant Name Receipt Configuration
  </p>
  <p class="text-muted mb-3">
    Choose how the applicant name is determined for the service receipt.
  </p>

  <p class="fw-semibold mb-1">Configuration type</p>

  <div class="row g-3">
    <div class="col-6">
      <div
        class="option-card p-3"
        role="button"
        tabindex="0"
        [class.option-card--selected]="configType === 'STATIC'"
        (click)="setConfigType('STATIC')"
        (keyup.enter)="setConfigType('STATIC')">
        <div class="d-flex align-items-start gap-3">
          <input
            type="radio"
            name="configType"
            class="form-check-input mt-1 flex-shrink-0"
            [checked]="configType === 'STATIC'"
            (click)="$event.stopPropagation(); setConfigType('STATIC')"
            (keyup.enter)="$event.stopPropagation(); setConfigType('STATIC')" />
          <div>
            <div class="d-flex align-items-center gap-2 mb-0">
              <i class="fas fa-layer-group text-primary"></i>
              <span class="fw-semibold">Static</span>
            </div>
            <small class="text-muted"
              >Always use the same field(s) for every applicant</small
            >
          </div>
        </div>
      </div>
    </div>

    <div class="col-6">
      <div
        class="option-card p-3"
        role="button"
        tabindex="0"
        [class.option-card--selected]="configType === 'DYNAMIC'"
        (click)="setConfigType('DYNAMIC')"
        (keyup.enter)="setConfigType('DYNAMIC')">
        <div class="d-flex align-items-start gap-3">
          <input
            type="radio"
            name="configType"
            class="form-check-input mt-1 flex-shrink-0"
            [checked]="configType === 'DYNAMIC'"
            (click)="$event.stopPropagation(); setConfigType('DYNAMIC')"
            (keyup.enter)="
              $event.stopPropagation(); setConfigType('DYNAMIC')
            " />
          <div>
            <div class="d-flex align-items-center gap-2 mb-0">
              <i class="fas fa-wand-magic-sparkles text-primary"></i>
              <span class="fw-semibold">Dynamic</span>
            </div>
            <small class="text-muted"
              >Use different fields based on conditions (e.g. applicant
              type)</small
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ─── Card 2a: Static configuration ──────────────────────────────────────── -->
<div class="card p-4 mb-4" *ngIf="configType === 'STATIC'">
  <div class="d-flex align-items-center gap-2 mb-1">
    <i class="fas fa-table-cells text-primary"></i>
    <p class="fw-semibold mb-0 section-title">
      Applicant Name Receipt Configuration
    </p>
  </div>
  <p class="text-muted mb-3">
    Configure which form field(s) will be displayed as the applicant name on the
    service receipt.
  </p>

  <!-- Configuration Mode -->
  <p class="fw-semibold mb-1">Configuration mode</p>
  <div class="row g-3 mb-4">
    <div class="col-6">
      <div
        class="option-card p-3"
        role="button"
        tabindex="0"
        [class.option-card--selected]="configMode === 'SINGLE'"
        (click)="setConfigMode('SINGLE')"
        (keyup.enter)="setConfigMode('SINGLE')">
        <div class="d-flex align-items-start gap-3">
          <input
            type="radio"
            name="configMode"
            class="form-check-input mt-1 flex-shrink-0"
            [checked]="configMode === 'SINGLE'"
            (click)="$event.stopPropagation(); setConfigMode('SINGLE')"
            (keyup.enter)="$event.stopPropagation(); setConfigMode('SINGLE')" />
          <div>
            <p class="fw-semibold mb-0">Single field</p>
            <small class="text-muted"
              >Use one form field as the applicant name</small
            >
          </div>
        </div>
      </div>
    </div>
    <div class="col-6">
      <div
        class="option-card p-3"
        role="button"
        tabindex="0"
        [class.option-card--selected]="configMode === 'COMBINED'"
        (click)="setConfigMode('COMBINED')"
        (keyup.enter)="setConfigMode('COMBINED')">
        <div class="d-flex align-items-start gap-3">
          <input
            type="radio"
            name="configMode"
            class="form-check-input mt-1 flex-shrink-0"
            [checked]="configMode === 'COMBINED'"
            (click)="$event.stopPropagation(); setConfigMode('COMBINED')"
            (keyup.enter)="
              $event.stopPropagation(); setConfigMode('COMBINED')
            " />
          <div>
            <p class="fw-semibold mb-0">Combined fields</p>
            <small class="text-muted"
              >Concatenate multiple fields in order</small
            >
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Single field dropdown -->
  <ng-container *ngIf="configMode === 'SINGLE'">
    <label class="form-label fw-semibold" for="singleFieldSelect"
      >Applicant name field</label
    >
    <ng-select
      [labelForId]="'singleFieldSelect'"
      [items]="availableFields"
      bindLabel="fieldLabel"
      bindValue="fieldName"
      placeholder="Select a field"
      [(ngModel)]="selectedField"
      class="mb-4 field-shadow">
    </ng-select>
  </ng-container>

  <!-- Combined fields: ordered list + Add field -->
  <ng-container *ngIf="configMode === 'COMBINED'">
    <p class="fw-semibold mb-3">Selected fields (in order)</p>

    <div class="mb-3">
      <div
        *ngFor="let fieldName of combinedFields; let i = index"
        class="field-row d-flex align-items-center gap-3 mb-2 p-2 border rounded">
        <span class="field-index-badge flex-shrink-0">{{ i + 1 }}</span>
        <div class="flex-grow-1">
          <p class="fw-semibold mb-0">{{ getFieldLabel(fieldName) }}</p>
          <small class="text-muted">{{ fieldName }}</small>
        </div>
        <button
          type="button"
          class="btn btn-link p-0 text-muted reorder-btn"
          [disabled]="i === 0"
          (click)="moveFieldUp(i)"
          title="Move up">
          <i class="fas fa-arrow-up"></i>
        </button>
        <button
          type="button"
          class="btn btn-link p-0 text-muted reorder-btn"
          [disabled]="i === combinedFields.length - 1"
          (click)="moveFieldDown(i)"
          title="Move down">
          <i class="fas fa-arrow-down"></i>
        </button>
        <button
          type="button"
          class="btn btn-link p-0 text-danger reorder-btn"
          (click)="removeCombinedField(i)"
          title="Remove">
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>

      <div
        *ngIf="combinedFields.length === 0"
        class="text-muted fst-italic small mb-2">
        No fields selected yet. Add a field below.
      </div>
    </div>

    <label class="form-label fw-semibold" for="addFieldSelect">Add field</label>
    <div class="mb-4">
      <ng-select
        [labelForId]="'addFieldSelect'"
        [items]="unselectedFields"
        bindLabel="fieldLabel"
        bindValue="fieldName"
        placeholder="Select a field to add"
        [(ngModel)]="newFieldToAdd"
        class="field-shadow"
        (change)="addCombinedField($any($event))">
      </ng-select>
    </div>
  </ng-container>

  <!-- Fallback field toggle -->
  <div class="fallback-section p-3 rounded mb-4">
    <div class="d-flex justify-content-between align-items-start">
      <div>
        <p class="fw-semibold mb-1">Fallback field</p>
        <small class="text-muted">
          Used when the
          {{
            configMode === 'COMBINED'
              ? 'combined fields are'
              : 'selected field is'
          }}
          empty on the application.
        </small>
      </div>
      <div class="form-check form-switch ms-3 flex-shrink-0">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="fallbackToggle"
          name="fallbackToggle"
          [attr.aria-checked]="fallbackEnabled"
          [(ngModel)]="fallbackEnabled"
          style="width: 3rem; height: 1.5rem; cursor: pointer" />
      </div>
    </div>
    <div class="mt-3" *ngIf="fallbackEnabled">
      <ng-select
        [items]="availableFields"
        bindLabel="fieldLabel"
        bindValue="fieldName"
        placeholder="Select a fallback field"
        [(ngModel)]="fallbackField"
        class="field-shadow">
      </ng-select>
    </div>
  </div>

  <!-- Action buttons -->
  <div class="d-flex justify-content-end gap-2">
    <button type="button" class="btn btn-primary px-4" (click)="onSave()">
      Save configuration
    </button>
  </div>
</div>

<!-- ─── Card 2b: Dynamic applicant name rules ───────────────────────────────── -->
<div class="card p-4" *ngIf="configType === 'DYNAMIC'">
  <div class="d-flex align-items-center gap-2 mb-1">
    <i class="fas fa-code-branch text-primary"></i>
    <p class="fw-semibold mb-0 section-title">Dynamic applicant name rules</p>
  </div>
  <p class="text-muted mb-4">
    Override the default applicant name with conditional scenarios (e.g.
    Business → Company name, Individual → Surname + Other names).
  </p>

  <!-- Scenario cards -->
  <div
    *ngFor="let scenario of scenarios; let si = index; trackBy: trackByIndex"
    class="scenario-card border rounded p-3 mb-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <span class="fw-semibold text-primary">Scenario {{ si + 1 }}</span>
      <button
        type="button"
        class="btn btn-link p-0 text-danger"
        (click)="removeScenario(si)">
        <i class="fas fa-trash-alt"></i>
      </button>
    </div>

    <!-- Set Applicant Name to -->
    <p class="fw-semibold mb-2">Set Applicant Name to</p>
    <div class="d-flex align-items-start gap-2 mb-3 flex-wrap">
      <!-- Field mode dropdown — fixed width to fit content -->
      <div style="width: 160px" class="flex-shrink-0">
        <ng-select
          class="field-shadow"
          [items]="fieldModeOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="scenario.fieldMode"
          (ngModelChange)="onScenarioFieldModeChange(scenario, $any($event))">
        </ng-select>
      </div>

      <!-- Single field selector -->
      <div class="flex-grow-1" *ngIf="scenario.fieldMode === 'SINGLE'">
        <ng-select
          [items]="availableFields"
          class="field-shadow"
          bindLabel="fieldLabel"
          bindValue="fieldName"
          placeholder="Select a field"
          [(ngModel)]="scenario.selectedSingleField"
          (ngModelChange)="onScenarioSingleFieldChange(scenario, $any($event))">
        </ng-select>
      </div>

      <!-- Combined: chips + add field -->
      <div
        *ngIf="scenario.fieldMode === 'COMBINED'"
        class="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
        <span
          *ngFor="
            let fn of scenario.sourceFields;
            let fi = index;
            trackBy: trackByIndex
          "
          class="combined-chip d-inline-flex align-items-center gap-1">
          {{ fi + 1 }}. {{ getFieldLabel(fn) }}
          <button
            type="button"
            class="btn-chip-remove"
            (click)="removeScenarioField(si, fi)">
            ×
          </button>
        </span>
        <div style="min-width: 160px">
          <ng-select
            [items]="scenarioUnselectedFields[si]"
            class="field-shadow"
            bindLabel="fieldLabel"
            bindValue="fieldName"
            placeholder="Add field"
            [(ngModel)]="scenario.newFieldToAdd"
            (change)="addScenarioField(si)">
          </ng-select>
        </div>
      </div>
    </div>

    <!-- Conditions — direct [(ngModel)] binding avoids array recreation on keypress -->
    <div
      *ngFor="
        let condition of scenario.conditions;
        let ci = index;
        trackBy: trackByIndex
      "
      class="d-flex align-items-center gap-2 mb-2 flex-wrap">
      <span class="text-muted condition-when-label">
        {{ ci === 0 ? 'When' : 'And' }}
      </span>
      <div style="min-width: 160px">
        <ng-select
          [items]="availableFields"
          bindLabel="fieldLabel"
          class="field-shadow"
          bindValue="fieldName"
          placeholder="Attribute"
          [(ngModel)]="condition.attribute">
        </ng-select>
      </div>
      <div style="min-width: 110px">
        <ng-select
          [items]="attributeTypeOptions"
          class="field-shadow"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="condition.attributeType">
        </ng-select>
      </div>
      <div style="min-width: 160px">
        <ng-select
          [items]="comparatorOptions"
          class="field-shadow"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [(ngModel)]="condition.comparator">
        </ng-select>
      </div>
      <div class="flex-grow-1" style="min-width: 100px">
        <input
          type="text"
          class="form-control condition-value field-shadow"
          placeholder="Value"
          [(ngModel)]="condition.value" />
      </div>
      <button
        *ngIf="scenario.conditions.length > 1"
        type="button"
        class="btn btn-link p-0 text-danger"
        (click)="removeCondition(scenario, ci)">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="mt-2">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary add-condition field-shadow"
        (click)="addCondition(scenario)">
        <i class="fas fa-plus me-1"></i>Add Condition
      </button>
    </div>
  </div>

  <!-- Add Scenario button -->
  <div class="mb-4">
    <button
      type="button"
      class="btn btn-primary btn-sm"
      (click)="addScenario()">
      <i class="fas fa-plus me-1"></i>Add Scenario
    </button>
  </div>

  <!-- Global fallback for Dynamic mode -->
  <div class="fallback-section p-3 rounded mb-4">
    <div class="d-flex justify-content-between align-items-start">
      <div>
        <p class="fw-semibold mb-1">Fallback field</p>
        <small class="text-muted">
          Used when the selected field(s) are empty for any scenario.
        </small>
      </div>
      <div class="form-check form-switch ms-3 flex-shrink-0">
        <input
          class="form-check-input"
          type="checkbox"
          role="switch"
          id="dynamicFallbackToggle"
          name="dynamicFallbackToggle"
          [attr.aria-checked]="dynamicFallbackEnabled"
          [(ngModel)]="dynamicFallbackEnabled"
          style="width: 3rem; height: 1.5rem; cursor: pointer" />
      </div>
    </div>
    <div class="mt-3" *ngIf="dynamicFallbackEnabled">
      <ng-select
        [items]="availableFields"
        bindLabel="fieldLabel"
        bindValue="fieldName"
        placeholder="Select a fallback field"
        [(ngModel)]="dynamicFallbackField">
      </ng-select>
    </div>
  </div>

  <!-- Action buttons -->
  <div class="d-flex justify-content-end gap-2">
    <button
      type="button"
      class="btn btn-primary px-4"
      (click)="onSaveDynamic()">
      Save rules
    </button>
  </div>
</div>
